<template>
  <div
    id="subscribe"
    class="subscribe-form"
  >
    <form @submit.prevent="submit">
      <h2>{{ formTitle }}</h2>
      <span
        v-if="formText"
        class="form-text"
      >
        {{ formText }}
      </span>

      <InputGroup
        v-model="form.name"
        :class="{ error: requiredNotFilled('name') }"
        :error="requiredNotFilled('name')"
        error-input-name="name"
        autocomplete="name"
        @enter-submit="submit"
      >
        Full name
      </InputGroup>
      <InputGroup
        v-model="form.email"
        error-input-name="email"
        type="email"
        :class="{ error: requiredNotFilled('email') }"
        :error="requiredNotFilled('email') || !isValidEmail"
        autocomplete="email"
        @enter-submit="submit"
      >
        Email
      </InputGroup>
      <SearchBar
        v-model="skillSearch"
        class="skill"
        :search-results="skillSearchResults"
        error-message="skill"
        autocomplete="off"
        @selected="selectSkill"
      >
        Select your primary coding language
      </SearchBar>

      <span
        v-if="error"
        class="error"
      >
        Something went wrong. Please try again.
      </span>
      <span class="privacy-policy-text">
        By submitting this form you agree to our
        <nuxt-link
          to="/privacy"
          target="_blank"
        >
          Privacy Policy.
        </nuxt-link>
      </span>
      <button
        class="btn proxify-btn proxify-btn-no-icon submit-button"
        :class="{ disabled: isLoading }"
        :disabled="isLoading"
      >
        <span
          v-if="isLoading"
          class="spin-content"
        >
          <SpinnerIcon />
          {{ buttonTitle }}
        </span>
        <span v-else>{{ buttonTitle }}</span>
      </button>
    </form>
  </div>
</template>

<script>
import InputGroup from '~/components/Common/UI/InputGroup/InputGroup';
import SearchBar from '~/components/Common/UI/SearchBar/SearchBar';
import SpinnerIcon from '~/components/Common/Svg/SpinnerIcon';
import { useSkillsStore } from '~/stores/skills';
import { useSubscriptionStore } from '~/stores/subscription';
import { validateEmail } from '~/utils/helpers/validators';

export default {
  components: {
    InputGroup,
    SearchBar,
    SpinnerIcon,
  },
  props: {
    formTitle: {
      type: String,
      default: 'Join Proxify!',
    },
    buttonTitle: {
      type: String,
      default: 'Download PDF',
    },
    formText: {
      type: String,
      default: null,
    },
  },
  emits: ['subscribed'],
  data() {
    return {
      form: {
        name: '',
        email: '',
        skill: null,
      },
      skillSearch: '',
      skillSearchResults: [],
      requireErrors: [],
      isLoading: false,
      isValidEmail: true,
      showRequireError: false,
      errorMessage: '',
      error: false,
      skillsStore: useSkillsStore(),
      subscriptionStore: useSubscriptionStore(),
    };
  },
  computed: {
    allSkills() {
      return this.skillsStore.skills;
    },
  },
  watch: {
    skillSearch(search) {
      if (search === '') {
        this.skillSearchResults = [];
        return;
      }
      this.skillSearchResults = this.allSkills.filter((item) =>
        item.name.toLowerCase().startsWith(search.toLowerCase())
      );
    },
  },
  methods: {
    selectSkill(value) {
      this.form.skill = value.name;
      this.skillSearch = value.name;
      this.$nextTick(() => {
        this.skillSearchResults = [];
      });
    },
    async submit() {
      if (this.isLoading) {
        return;
      }
      this.requireErrors = [];
      if (this.checkForRequireErrors()) {
        return;
      }
      if (!validateEmail(this.form.email)) {
        this.isValidEmail = false;
        return;
      }

      this.isLoading = true;

      try {
        await this.subscriptionStore.subscribeByAudience({
          ...this.form,
          subscribed_from: 'lead-gen-page',
          g_recaptcha: await this.$recaptchaV3('leadgen_form'),
        });

        this.success = true;
        this.$emit('subscribed');

        this.$analytics.track('CTA Clicked', {
          cta_name: this.buttonTitle,
          section: 'Hero section',
          page_name: 'Lead gen page v1',
          name: this.form.name,
          email: this.form.email,
          coding_language: this.form.skill,
        });
      } catch (error) {
        this.error = true;
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    checkForRequireErrors() {
      ['name', 'email'].forEach((field) => {
        if (
          !this.form[field] ||
          this.form[field] === '' ||
          (typeof this.form[field] === 'object' && !this.form[field].length)
        ) {
          this.requireErrors.push(field);
        }
      });

      return !!this.requireErrors.length;
    },
    requiredNotFilled(field) {
      return !!this.requireErrors.includes(field);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './SubscribeForm.scss';
</style>
